import {
  defaultShouldDehydrateQuery,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import superjson from 'superjson';
import { toast } from 'sonner';

const isServer = typeof window === 'undefined';

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, set a non-zero staleTime so the client considers
        // server-prefetched data fresh and skips an immediate refetch
        // after hydration (30 seconds)
        staleTime: 30 * 1_000,
        // Only throw an error if the query has *no* cached data (i.e. data === undefined).
        // This lets components continue to render with stale data while background refetches
        // can still surface errors via onError handlers or error boundaries when necessary.
        throwOnError: (_, query) => typeof query.state.data === 'undefined',
      },
      dehydrate: {
        serializeData: superjson.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === 'pending',
      },
      hydrate: {
        deserializeData: superjson.deserialize,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only show error toasts for failed background refetches
        // (when we already have cached data). Initial load failures
        // are handled by error boundaries.
        if (query.state.data !== undefined) {
          console.error(error);
          toast.error('Något gick fel');
        }
      },
    }),
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  }

  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  return (browserQueryClient ??= makeQueryClient());
}
